/**
 * Custom Product Media Slider
 * Simple, clean vanilla JS slider
 */

class PMSlider {
  constructor(element) {
    this.slider = element;
    this.track = element.querySelector('.pm-slider__track');
    this.slides = Array.from(element.querySelectorAll('.pm-slide:not(.pm-slide--filtered)'));
    this.totalSlides = this.slides.length;
    
    if (this.totalSlides <= 1) {
      this.showSlider();
      return;
    }
    
    // Settings
    this.currentIndex = 0;
    this.fadeMode = element.dataset.fade === 'true';
    this.isDragging = false;
    
    // Find initial slide
    const initialSlide = element.querySelector('.pm-slide--initial');
    if (initialSlide) {
      this.currentIndex = this.slides.indexOf(initialSlide);
    }
    
    this.init();
  }
  
  init() {
    this.isReady = false;
    
    this.setupSlides();
    this.setupNavigation();
    this.setupTouch();
    this.setupClickNav();
    this.setupKeyboard();
    
    // Show initial slide first
    this.goTo(this.currentIndex, false);
    
    // Mark as ready immediately - no delay to prevent flash
    this.slider.classList.add('pm-slider--ready');
    this.isReady = true;
    this.hideHero();
    this.emit('ready');
  }
  
  setupSlides() {
    if (this.fadeMode) {
      // Fade mode: stack slides, make initial one visible
      this.slides.forEach((slide, index) => {
        slide.style.position = index === this.currentIndex ? 'relative' : 'absolute';
        slide.style.opacity = index === this.currentIndex ? '1' : '0';
        slide.style.visibility = index === this.currentIndex ? 'visible' : 'hidden';
        if (index === this.currentIndex) {
          slide.classList.add('pm-slide--active');
        }
      });
    } else {
      // Slide mode: flex layout
      this.slides.forEach(slide => {
        slide.style.flex = '0 0 100%';
      });
    }
  }
  
  setupNavigation() {
    // Button clicks
    document.addEventListener('click', (e) => {
      const nav = e.target.closest(`[data-slider-id="${this.slider.id}"]`);
      if (!nav) return;
      
      if (e.target.closest('[data-action="prev"]')) {
        this.prev();
      } else if (e.target.closest('[data-action="next"]')) {
        this.next();
      } else if (e.target.closest('[data-action="select"]')) {
        const mediaId = e.target.closest('[data-media-id]')?.dataset.mediaId;
        if (mediaId) this.selectByMediaId(mediaId);
      }
    });
    
    // Thumbnail clicks
    document.addEventListener('click', (e) => {
      const thumb = e.target.closest('.product__thumbnail-item:not(.pm-slide--filtered)');
      if (thumb) {
        const mediaId = thumb.dataset.mediaId;
        if (mediaId) this.selectByMediaId(mediaId);
      }
    });
    
    // Dots clicks
    document.addEventListener('click', (e) => {
      const dot = e.target.closest('.dots-nav__item:not(.pm-slide--filtered)');
      if (dot) {
        const mediaId = dot.dataset.mediaId;
        if (mediaId) this.selectByMediaId(mediaId);
      }
    });
  }
  
  setupTouch() {
    if (this.fadeMode) return;
    
    let startX = 0;
    let currentX = 0;
    let isDragging = false;
    let startTransform = 0;
    let hasMoved = false;
    
    const getTransform = () => {
      // Get current transform based on currentIndex to avoid glitches
      return -this.currentIndex * this.slider.offsetWidth;
    };
    
    const onStart = (e) => {
      if (!this.isReady) return;
      
      const point = e.touches ? e.touches[0] : e;
      startX = point.clientX;
      currentX = startX;
      hasMoved = false;
      
      // Get the actual current position
      startTransform = getTransform();
      
      // Disable transitions for smooth dragging
      this.track.style.transition = 'none';
      this.slider.classList.add('pm-slider--dragging');
    };
    
    const onMove = (e) => {
      if (!this.slider.classList.contains('pm-slider--dragging')) return;
      
      const point = e.touches ? e.touches[0] : e;
      currentX = point.clientX;
      const diff = currentX - startX;
      
      // Detect horizontal movement
      if (Math.abs(diff) > 3) {
        hasMoved = true;
        isDragging = true;
        
        // Apply transform directly based on current position
        const newTransform = startTransform + diff;
        this.track.style.transform = `translateX(${newTransform}px)`;
        
        // Prevent default to stop page scrolling
        if (e.cancelable) {
          e.preventDefault();
        }
      }
    };
    
    const onEnd = () => {
      this.slider.classList.remove('pm-slider--dragging');
      
      // Re-enable transitions
      this.track.style.transition = '';
      
      if (isDragging && hasMoved) {
        const diff = currentX - startX;
        const threshold = this.slider.offsetWidth * 0.15; // 15% of width
        
        if (Math.abs(diff) > threshold) {
          diff > 0 ? this.prev() : this.next();
        } else {
          // Snap back to current slide
          this.goTo(this.currentIndex, true);
        }
      }
      
      isDragging = false;
      hasMoved = false;
    };
    
    this.slider.addEventListener('mousedown', onStart);
    this.slider.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('mousemove', onMove);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchend', onEnd);
  }
  
  setupClickNav() {
    const leftArea = document.createElement('div');
    leftArea.className = 'pm-nav-area pm-nav-area--left';
    
    const rightArea = document.createElement('div');
    rightArea.className = 'pm-nav-area pm-nav-area--right';
    
    this.slider.appendChild(leftArea);
    this.slider.appendChild(rightArea);
    
    leftArea.addEventListener('click', () => this.prev());
    rightArea.addEventListener('click', () => this.next());
    
    // Hover effects
    this.slider.addEventListener('mousemove', (e) => {
      const rect = this.slider.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const isLeft = x < rect.width / 2;
      
      this.slider.classList.toggle('pm-slider--hover-left', isLeft);
      this.slider.classList.toggle('pm-slider--hover-right', !isLeft);
    });
    
    this.slider.addEventListener('mouseleave', () => {
      this.slider.classList.remove('pm-slider--hover-left', 'pm-slider--hover-right');
    });
  }
  
  setupKeyboard() {
    this.slider.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prev();
        e.preventDefault();
      } else if (e.key === 'ArrowRight') {
        this.next();
        e.preventDefault();
      }
    });
  }
  
  goTo(index, animate = true) {
    if (index < 0 || index >= this.totalSlides) return;
    
    const prevIndex = this.currentIndex;
    this.currentIndex = index;
    
    if (this.fadeMode) {
      // Fade transition: CSS handles positioning via classes
      this.slides.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle('pm-slide--active', isActive);
        
        // Ensure proper inline styles for reliability
        if (isActive) {
          slide.style.position = 'relative';
          slide.style.opacity = '1';
          slide.style.visibility = 'visible';
          slide.style.zIndex = '2';
        } else {
          slide.style.position = 'absolute';
          slide.style.opacity = '0';
          slide.style.visibility = 'hidden';
          slide.style.zIndex = '1';
        }
      });
    } else {
      // Slide transition
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('pm-slide--active', i === index);
      });
      
      const translateX = -index * 100;
      this.track.style.transition = animate ? 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none';
      this.track.style.transform = `translateX(${translateX}%)`;
    }
    
    this.updateNav();
    this.preloadNext();
    
    if (prevIndex !== index) {
      this.emit('change', { currentIndex: index, previousIndex: prevIndex });
    }
  }
  
  updateNav() {
    const currentSlide = this.slides[this.currentIndex];
    if (!currentSlide) return;
    
    const mediaId = currentSlide.dataset.mediaId;
    
    // Update all navigation
    document.querySelectorAll('.product__thumbnail-item, .dots-nav__item').forEach(el => {
      const isActive = el.dataset.mediaId === mediaId;
      el.setAttribute('aria-current', isActive ? 'true' : 'false');
      
      // Scroll active thumbnail into view
      if (isActive && el.classList.contains('product__thumbnail-item')) {
        this.scrollThumbnailIntoView(el);
      }
    });
  }
  
  scrollThumbnailIntoView(thumbnail) {
    const scrollContainer = thumbnail.closest('.product__thumbnail-list');
    if (!scrollContainer) return;
    
    const containerRect = scrollContainer.getBoundingClientRect();
    const thumbRect = thumbnail.getBoundingClientRect();
    
    // Check if thumbnail is outside visible area
    if (thumbRect.left < containerRect.left || thumbRect.right > containerRect.right) {
      // Calculate scroll position to center the thumbnail
      const scrollLeft = thumbnail.offsetLeft - (scrollContainer.offsetWidth / 2) + (thumbnail.offsetWidth / 2);
      scrollContainer.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  }
  
  preloadNext() {
    const nextIndex = (this.currentIndex + 1) % this.totalSlides;
    const nextSlide = this.slides[nextIndex];
    
    if (nextSlide) {
      const img = nextSlide.querySelector('img');
      if (img && img.loading === 'lazy') {
        img.loading = 'eager';
      }
    }
  }
  
  selectByMediaId(mediaId) {
    const index = this.slides.findIndex(s => s.dataset.mediaId === mediaId);
    if (index !== -1) this.goTo(index);
  }
  
  next() {
    this.goTo((this.currentIndex + 1) % this.totalSlides);
  }
  
  prev() {
    this.goTo((this.currentIndex - 1 + this.totalSlides) % this.totalSlides);
  }
  
  showSlider() {
    this.slider.classList.add('pm-slider--ready');
    this.hideHero();
  }
  
  hideHero() {
    const heroId = this.slider.id.replace('slider-', 'hero-');
    const hero = document.getElementById(heroId);
    
    if (hero) {
      hero.classList.add('pm-hero--hidden');
      setTimeout(() => {
        hero.classList.add('pm-hero--removed');
      }, 400);
    }
  }
  
  emit(event, data = {}) {
    this.slider.dispatchEvent(new CustomEvent(`pm-slider:${event}`, {
      detail: { slider: this, ...data }
    }));
  }
}

/**
 * Initialize all sliders
 */
function initProductSliders() {
  document.querySelectorAll('.pm-slider').forEach(slider => {
    if (!slider.pmSliderInstance) {
      slider.pmSliderInstance = new PMSlider(slider);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductSliders);
} else {
  initProductSliders();
}

// Re-initialize on section load (Shopify theme editor)
document.addEventListener('shopify:section:load', initProductSliders);

// Export for global access
window.PMSlider = PMSlider;
window.initProductSliders = initProductSliders;